'use client';
import React, { createContext, useState, useContext, ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { ModelCloseIcon } from '@whelk/assets/src/web/common-icons';
import './index.scss';

// 定义确认框选项接口
interface ConfirmOptions {
  title?: ReactNode;
  content: ReactNode;
  onOk?: () => void;
  onCancel?: () => void;
}

// 定义上下文类型
interface ConfirmContextType {
  showConfirm: (options: ConfirmOptions) => void;
  hideConfirm: () => void;
  confirmState: ConfirmOptions & { open: boolean };
}

// 创建上下文
const ConfirmContext = createContext<ConfirmContextType | null>(null);

// 自定义 hook
export const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm 必须在 ConfirmProvider 内部使用');
  }
  return context;
};

// Provider 组件
export const ConfirmProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [confirmState, setConfirmState] = useState<ConfirmOptions & { open: boolean }>({
    open: false,
    title: '',
    content: '',
  });

  const showConfirm = useCallback((options: ConfirmOptions) => {
    setConfirmState({ ...options, open: true });
  }, []);

  const hideConfirm = useCallback(() => {
    setConfirmState((prev) => ({ ...prev, open: false }));
  }, []);

  return (
    <ConfirmContext.Provider value={{ showConfirm, hideConfirm, confirmState }}>
      {children}
      <GlobalConfirm />
    </ConfirmContext.Provider>
  );
};

// 确认框组件
const GlobalConfirm = () => {
  const { t } = useTranslation();
  const { confirmState, hideConfirm } = useConfirm();
  const { open, title, content, onOk, onCancel } = confirmState;

  const handleCancel = useCallback(() => {
    onCancel?.();
    hideConfirm();
  }, [hideConfirm, onCancel]);

  const handleConfirm = useCallback(() => {
    onOk?.();
    hideConfirm();
  }, [hideConfirm, onOk]);

  if (!open) return null;

  return (
    <section className='fixed bottom-0 left-0 right-0 top-0 z-[1100]'>
      <div
        className='bg-col_mask01 flex min-h-full w-full items-center justify-center p-8 pb-10 '
        onClick={handleCancel}
      >
        <div onClick={(e) => e.stopPropagation()} className=' md:w-auto'>
          <div
            className={classNames(
              'text-col_text02 bg-col_bg09 relative w-[280px] overflow-hidden rounded-[20px] pt-6 text-[16px] md:w-[476px] md:max-w-full',
            )}
          >
            <div className='flex items-center justify-center px-5  md:justify-between md:px-6'>
              <div className='text-col_text00 pb-3 text-[16px] font-[500]  md:pb-4'>{title}</div>
              <span
                className={`md:hover:bg-col_text05 text-col_text02 md:text-col_text03 absolute right-4 top-4 z-50  hidden h-[30px] w-[30px] cursor-pointer items-center justify-center rounded md:right-6 md:top-6 md:flex`}
                onClick={handleCancel}
              >
                <ModelCloseIcon />
              </span>
            </div>

            <div className='text-col_text02 px-5 pb-6 text-center text-[14px] md:px-6 md:pb-8  md:text-left'>
              {content}
            </div>
            <div className='border-col_line00 flex  w-full border-t pb-0 text-[14px] leading-[20px] md:justify-end md:gap-4 md:border-none md:px-6 md:pb-6'>
              <button
                className='md:bg-col_bg14 text-col_text06 md:hover:bg-col_bg01 h-[44px] w-[50%] min-w-[80px] rounded-none px-3 hover:bg-transparent md:h-[36px] md:w-auto md:rounded-[8px]'
                onClick={handleCancel}
              >
                {t('general_cancel')}
              </button>
              <button
                className='text-col_accent01 border-col_line00 red md:text-col_text05  h-[44px] w-[50%] min-w-[80px] rounded-none border-l px-3 md:h-[36px] md:w-auto md:rounded-[8px]'
                onClick={handleConfirm}
              >
                {t('general_delete')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// 导出组件以及 hook
export default ConfirmProvider;
